import moment from 'moment-timezone';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      lib util — keystroke-tolerant parser for a typed date (slashes, dashes,
 *              dots, compact digits, month names) -> moment + canonical display string
 *   extends:   -
 *   template:  none
 *   args:      -
 *   exports:   parseTypedDate(value, { displayFormat = 'DD/MM/YYYY' } = {})
 *                -> { isValid, date, formatted }  (invalid: { isValid:false, date: moment.invalid() })
 *              parseCompactNumericDate(value) -> moment (moment.invalid() when not a date)
 *   uses:      moment-timezone (strict parsing, current global locale for month names)
 *   siblings:  beebee-component-input/components/bb-input-date.js (_parseAndValidateDate /
 *              _parseCompactNumericDate delegate here), beebee-base-template bb-period-panel
 *              (typed Dal/Al fields), ./period.js
 *   tests:     tests/unit/lib/date-parse-test.js; parity guard = the bb-input-date unit test
 *              in beebee-component-input (module "bb-input-date (parser)", drives the
 *              delegating methods on the prototype)
 *   gotchas:   VERBATIM move of the bb-input-date parser: format list, order and the
 *              commented-out moment(value) auto-parse fallback are kept on purpose (the
 *              auto-parse accepted garbage like "1" as a date). `displayFormat` replaces
 *              the component's `this.momentFormat`; it is tried first and is also the
 *              output format. Ambiguous US formats (M/D/YYYY) come AFTER the European
 *              ones, so 03/04/2026 is 3 April. Pure: no Ember, no DOM.
 */

// Unified parsing and validation of a typed date
function parseTypedDate(value, {
  displayFormat = 'DD/MM/YYYY'
} = {}) {
  // Fast-path: the 6 formats that cover >99% of real-world input in the primary locale.
  // Checked first to avoid iterating all 37 formats on every keystroke.
  const fastFormats = [displayFormat,
  // DD/MM/YYYY
  'D/M/YYYY', 'DD/MM/YY', 'YYYY-MM-DD', 'DDMMYYYY', 'DDMMYY'];
  for (const format of fastFormats) {
    const date = moment(value, format, true);
    if (date.isValid()) {
      return {
        isValid: true,
        date,
        formatted: date.format(displayFormat)
      };
    }
  }

  // Full fallback list for edge-case formats
  const allFormats = ['D/M/YY', 'DD-MM-YYYY', 'D-M-YYYY', 'D-M-YY', 'DD-MM-YY', 'D.M.YYYY', 'DD.MM.YYYY', 'D.M.YY', 'DD.MM.YY', 'YYYY/MM/DD', 'YYYY.MM.DD', 'YY-MM-DD', 'YY/MM/DD', 'YY.MM.DD', 'DMMYYYY', 'YYYYMMDD', 'DMMYY', 'YYMMDD', 'M/D/YYYY', 'MM/DD/YYYY', 'M/D/YY', 'MM/DD/YY', 'MMMM D, YYYY', 'MMM D, YYYY', 'MMMM D, YY', 'MMM D, YY', 'D MMM YYYY', 'D MMMM YYYY', 'D MMM YY', 'D MMMM YY', 'ddd D MMM YYYY', 'ddd D MMM YY'];
  for (const format of allFormats) {
    const date = moment(value, format, true);
    if (date.isValid()) {
      return {
        isValid: true,
        date: date,
        formatted: date.format(displayFormat)
      };
    }
  }

  // Compact numeric formats
  if (/^\d{4,8}$/.test(value)) {
    const numericDate = parseCompactNumericDate(value);
    if (numericDate.isValid()) {
      return {
        isValid: true,
        date: numericDate,
        formatted: numericDate.format(displayFormat)
      };
    }
  }

  // Automatic parsing fallback
  /*if (value) {
    const autoDate = moment(value);
    if (autoDate.isValid()) {
      return {
        isValid: true,
        date: autoDate,
        formatted: autoDate.format(displayFormat),
      };
    }
  }*/

  return {
    isValid: false,
    date: moment.invalid()
  };
}

// Compact numeric date parsing helper
function parseCompactNumericDate(value) {
  let day, month, year;
  switch (value.length) {
    case 5:
      // DMMYY
      day = value.slice(0, 1);
      month = value.slice(1, 3);
      year = value.slice(3);
      break;
    case 6:
      // DDMMYY
      day = value.slice(0, 2);
      month = value.slice(2, 4);
      year = value.slice(4);
      break;
    case 7:
      // DMMYYYY
      day = value.slice(0, 1);
      month = value.slice(1, 3);
      year = value.slice(3);
      break;
    case 8:
      // DDMMYYYY
      day = value.slice(0, 2);
      month = value.slice(2, 4);
      year = value.slice(4);
      break;
    default:
      return moment.invalid();
  }
  const dayNum = parseInt(day, 10);
  const monthNum = parseInt(month, 10);

  // Basic validation
  if (dayNum < 1 || dayNum > 31 || monthNum < 1 || monthNum > 12) {
    return moment.invalid();
  }
  const format = year.length === 2 ? 'D/M/YY' : 'D/M/YYYY';
  const result = moment(`${day}/${month}/${year}`, format, true);
  return result;
}

export { parseCompactNumericDate, parseTypedDate };
//# sourceMappingURL=date-parse.js.map
